import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

function getWeekOfMonth(dateString) {
  const date = new Date(dateString);
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const dayOfWeek = startOfMonth.getDay();
  const adjustedDate = date.getDate() + dayOfWeek;
  return Math.ceil(adjustedDate / 7);
}

export const handleExportToPDF = (
  kitchenTasks = [],
  activityTasks = [],
  auditTasks = [],
  bedroomTasks = [],
  firstAidTasks = [],
  freezerTasks = [],
  keysTasks = [],
  moneyTasks = [],
  productTasks = [],
  showerTasks = [],
  vehicleTasks = [],
  waterTasks = [],
  taskName
) => {
  if (
    ![
      kitchenTasks,
      activityTasks,
      auditTasks,
      bedroomTasks,
      firstAidTasks,
      freezerTasks,
      keysTasks,
      moneyTasks,
      productTasks,
      showerTasks,
      vehicleTasks,
      waterTasks,
    ].some((tasks) => tasks.length > 0)
  ) {
    alert("No tasks available to export.");
    return;
  }

  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(18);
  doc.text(`${taskName} Task Report`, 14, 20);
  let startY = 30;

  // Export Kitchen Tasks
  if (kitchenTasks.length > 0) {
    const kitchenTableData = kitchenTasks.flatMap((task) =>
      task.items.map((item) => [
        taskName,
        task.time || "N/A",
        task.completedBy || "Pending",
        item.itemName || "N/A",
        item.itemCount || "N/A",
        task.updatedAt ? format(new Date(task.updatedAt), "dd MMM yyyy") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Time", "Completed By", "Item Name", "Item Count", "Updated Date"]],
      body: kitchenTableData,
      startY,
      styles: { fontSize: 10 },
    });

    startY = doc.lastAutoTable.finalY + 10;
  }

  // Export Activity Tasks
  if (activityTasks.length > 0) {
    const activityTableData = activityTasks.flatMap((tasksByDate) =>
      tasksByDate[1].map((task) => [
        taskName,
        format(new Date(task.date), "dd MMM yyyy"),
        `Week ${getWeekOfMonth(task.date)}`,
        task.planned_appointments || "N/A",
        task.actual_appointments || "N/A",
        task.repeat_type || "N/A",
        task.start_time ? format(new Date(task.start_time), "HH:mm") : "All Day",
        task.end_time ? format(new Date(task.end_time), "HH:mm") : "All Day",
        task.all_day ? "Yes" : "No",
        task.planned_activity || "N/A",
        task.comment || "N/A",
        task.completed_by || "Pending",
      ])
    );

    autoTable(doc, {
      head: [
        [
          "Task Name",
          "Date",
          "Week",
          "Planned Appointments",
          "Actual Appointments",
          "Repeat Type",
          "Start Time",
          "End Time",
          "All Day",
          "Planned Activity",
          "Comment",
          "Completed By",
        ],
      ],
      body: activityTableData,
      startY,
      styles: { fontSize: 10 },
      theme: "striped",
      tableWidth: "wrap",
    });

    startY = doc.lastAutoTable.finalY + 10;
  }

  // Export Audit Tasks
  if (auditTasks.length > 0) {
    const auditTableData = auditTasks.flatMap((task) =>
      task.questions.map((question) => [
        taskName,
        task.questionType || "N/A",
        question.date ? format(new Date(question.date), "dd MMM yyyy") : "N/A",
        question.section || "N/A",
        question.question || "N/A",
        question.condition || "N/A",
        question.comments || "N/A",
        task.completed_by || "Pending",
        task.created_at ? format(new Date(task.created_at), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [
        [
          "Task Name",
          "Question Type",
          "Date",
          "Section",
          "Question",
          "Condition",
          "Comments",
          "Completed By",
          "Created Date",
        ],
      ],
      body: auditTableData,
      startY,
      styles: { fontSize: 10 },
    });

    startY = doc.lastAutoTable.finalY + 10;
  }

  // Export Bedroom Tasks
  if (bedroomTasks.length > 0) {
    const bedroomTableData = bedroomTasks.flatMap((task) =>
      task.data.map((entry) => [
        taskName,
        entry.date ? format(new Date(entry.date), "dd MMM yyyy") : "N/A",
        entry.question || "N/A",
        entry.condition || "N/A",
        entry.comments || "N/A",
        task.completed_by || "Pending",
        task.created_at ? format(new Date(task.created_at), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Date", "Question", "Condition", "Comments", "Completed By", "Created At"]],
      body: bedroomTableData,
      startY,
      styles: { fontSize: 10 },
    });

    startY = doc.lastAutoTable.finalY + 10;
  }

  // Export First Aid Tasks
  if (firstAidTasks.length > 0) {
    const firstAidTableData = firstAidTasks.flatMap((task) =>
      task.items.map((item) => [
        taskName,
        item.itemName || "N/A",
        item.itemCount || "N/A",
        task.completedBy || "Pending",
        task.createdAt ? format(new Date(task.createdAt), "dd MMM yyyy HH:mm") : "N/A",
        task.updatedAt ? format(new Date(task.updatedAt), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Item Name", "Item Count", "Completed By", "Created At", "Updated At"]],
      body: firstAidTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Freezer Tasks
  if (freezerTasks.length > 0) {
    const freezerTableData = freezerTasks.flatMap(([date, tasks]) =>
      tasks.map((task) => [
        taskName,
        format(new Date(task.date), "dd MMM yyyy"),
        task.time || "N/A",
        task.fridge_temp || "N/A",
        task.freezer_temp || "N/A",
        task.comments || "N/A",
        task.completed_by || "Pending",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Date", "Time", "Fridge Temp", "Freezer Temp", "Comments", "Completed By"]],
      body: freezerTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Key Tasks
  if (keysTasks.length > 0) {
    const keysTableData = keysTasks.flatMap(([date, tasks]) =>
      tasks.map((task) => [
        taskName,
        format(new Date(task.date), "dd MMM yyyy"),
        task.key_no || "N/A",
        task.key_category || "N/A",
        task.employee_name || "N/A",
        task.pickup_time || "N/A",
        task.dropoff_time || "Not Returned",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Date", "Key No", "Key Category", "Employee Name", "Pickup Time", "Dropoff Time"]],
      body: keysTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Money Management Tasks
  if (moneyTasks.length > 0) {
    const moneyTableData = moneyTasks.flatMap(([date, tasks]) =>
      tasks.map((task) => [
        taskName,
        format(new Date(task.date), "dd MMM yyyy"),
        task.tab || "N/A",
        task.money_given !== null ? `£${task.money_given}` : "N/A",
        task.money_used !== null ? `£${task.money_used}` : "N/A",
        task.money_owed !== null ? `£${task.money_owed}` : "N/A",
        `£${task.balance || "0.00"}`,
        task.note || "N/A",
        task.completed_by || "Pending",
      ])
    );

    autoTable(doc, {
      head: [
        [
          "Task Name",
          "Date",
          "Tab",
          "Child Name",
          "Money Given",
          "Money Used",
          "Money Owed",
          "Balance",
          "Note",
          "Completed By",
        ],
      ],
      body: moneyTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Product Inventory Tasks
  if (productTasks.length > 0) {
    const productTableData = productTasks.flatMap((task) =>
      task.products.map((product) => [
        taskName,
        format(new Date(product.date), "dd MMM yyyy"),
        product.product || "N/A",
        product.amount || "N/A",
        product.quantity || "N/A",
        product.comments || "N/A",
        task.completed_by || "Pending",
        task.created_at ? format(new Date(task.created_at), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Date", "Product", "Amount", "Quantity", "Comments", "Completed By", "Created At"]],
      body: productTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Shower Maintenance Tasks
  if (showerTasks.length > 0) {
    const showerTableData = showerTasks.flatMap(([date, tasks]) =>
      tasks.map((task) => [
        taskName,
        format(new Date(task.date), "dd MMM yyyy"),
        task.descale_status || "N/A",
        task.reason || "N/A",
        task.completed_by || "Pending",
        task.created_at ? format(new Date(task.created_at), "dd MMM yyyy HH:mm") : "N/A",
        task.updated_at ? format(new Date(task.updated_at), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [["Task Name", "Date", "Descale Status", "Reason", "Completed By", "Created At", "Updated At"]],
      body: showerTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Vehicle Inspection Tasks
  if (vehicleTasks.length > 0) {
    const vehicleTableData = vehicleTasks.flatMap((task) =>
      task.data.map((item) => [
        taskName,
        item.date ? format(new Date(item.date), "dd MMM yyyy") : "N/A",
        item.question || "N/A",
        item.condition || "N/A",
        item.damages || "N/A",
        item.damageType || "N/A",
        item.comments || "N/A",
        task.completed_by || "Pending",
        task.created_at ? format(new Date(task.created_at), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [
        [
          "Task Name",
          "Date",
          "Question",
          "Condition",
          "Damages",
          "Damage Type",
          "Comments",
          "Completed By",
          "Created At",
        ],
      ],
      body: vehicleTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  // Export Water Temperature Monitoring Tasks
  if (waterTasks.length > 0) {
    const waterTableData = waterTasks.flatMap(([date, tasks]) =>
      tasks.map((task) => [
        taskName,
        format(new Date(date), "dd MMM yyyy"), // Use the provided date for each task group
        task.area || "N/A",
        task.time || "N/A",
        task.hot_temp !== null ? `${task.hot_temp}°C` : "N/A",
        task.cold_temp !== null ? `${task.cold_temp}°C` : "N/A",
        task.hot_action || "N/A",
        task.cold_action || "N/A",
        task.completed_by || "Pending",
        task.created_at ? format(new Date(task.created_at), "dd MMM yyyy HH:mm") : "N/A",
      ])
    );

    autoTable(doc, {
      head: [
        [
          "Task Name",
          "Date",
          "Area",
          "Time",
          "Hot Temperature",
          "Cold Temperature",
          "Hot Action",
          "Cold Action",
          "Completed By",
          "Created At",
        ],
      ],
      body: waterTableData,
      startY,
      styles: { fontSize: 10 },
    });
  }

  const fileName = `${taskName}-${format(new Date(), "dd-MMM-yyyy")}.pdf`;
  doc.save(fileName);
};
